import { isString } from "mathjs";
import { IColor } from "../../interfaces/Misc";

export default class ColorUtils
{
	/**
	 * check if given colors a and b are identical by comparing each of their RGB components
	 * @static
	 * @param {IColor} a
	 * @param {IColor} b
	 * @returns true if both colors have euqal RGB values
	 * @memberof ColorUtils
	*/
	static areColorsEqualRGB(a: IColor, b: IColor)
	{
		return a.r === b.r && a.g === b.g && a.b === b.b;
	}

	/**
	 * creates a random RGB color for CSS styles, with optional lower bounds
	 * @static
	 * @param {Number} [lower=180] an optional lower bound per RGB color component
	 * @returns a random RGB color formatted in CSS style "rgb(rrr, ggg, bbb)"
	 * @memberof ColorUtils
	*/
	static getRandomColorRGB(lower = 180)
	{
		const range = 255 - lower;
		return "rgb(" +
			Math.floor(lower + Math.random() * range) + "," +
			Math.floor(lower + Math.random() * range) + "," +
			Math.floor(lower + Math.random() * range) + ")";
	}

	/**
	 * taken straight out of threejs.color
	 *
	 * @static
	 * @param {number} c
	 * @returns
	 * @memberof ColorUtils
	 */
	static SRGBToLinear(c: number)
	{
		return (c < 0.04045) ? c * 0.0773993808 : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4);
	}

	static helperCanvas: HTMLCanvasElement | null = null;
	static helperContext: CanvasRenderingContext2D | null = null;
	/**
	 * 
	 *
	 * @param {string} cssColor
	 * @returns {(ColorAlpha | null)}
	 */
	static cssColorToHexAlpha(cssColor: string): { color: string, alpha: number } 
	{
		// Normalize input to handle case sensitivity and whitespace
		cssColor = cssColor.trim().toLowerCase();

		// Direct parsing for RGBA and RGB colors
		const rgbaRegex = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([01]|0?\.\d+))?\)/;
		const rgbaMatch = cssColor.match(rgbaRegex);
		if (rgbaMatch) 
		{
			const r = parseInt(rgbaMatch[1], 10);
			const g = parseInt(rgbaMatch[2], 10);
			const b = parseInt(rgbaMatch[3], 10);
			const a = rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1;
			const color = `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
			return { color, alpha: Math.round(a * 100) / 100 }; // Ensures alpha is rounded to at most 2 decimal places
		}

		// Handling short hex colors (e.g., #0f0 -> #00ff00)
		const shortHexRegex = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/;
		const shortHexMatch = cssColor.match(shortHexRegex);
		if (shortHexMatch) 
		{
			const color = `#${shortHexMatch[1]}${shortHexMatch[1]}${shortHexMatch[2]}${shortHexMatch[2]}${shortHexMatch[3]}${shortHexMatch[3]}`;
			return { color, alpha: 1 };
		}

		// Handling full hex colors directly (e.g., #00ff00)
		const fullHexRegex = /^#([0-9a-f]{6})$/;
		if (fullHexRegex.test(cssColor)) 
		{
			return { color: cssColor, alpha: 1 };
		}

		// Fallback for named colors or other formats using canvas
		if (!this.helperCanvas) 
		{
			this.helperCanvas = document.createElement('canvas');
			this.helperCanvas.width = this.helperCanvas.height = 1;
			this.helperContext = this.helperCanvas.getContext('2d');
		}

		if (!this.helperContext) 
		{
			return { color: "#000000", alpha: 1 }; // Fallback if canvas context is not available
		}

		// Attempt to set the fillStyle with the provided cssColor and extract the RGBA values
		this.helperContext.clearRect(0, 0, 1, 1);
		this.helperContext.fillStyle = cssColor;
	
		this.helperContext.fillRect(0, 0, 1, 1);
		const data = this.helperContext.getImageData(0, 0, 1, 1).data;
		if (data[3] === 0) 
		{ // If alpha is 0, it means the color was invalid and canvas was not filled
			return { color: "#000000", alpha: 1 };
		}
		const rgbToHex = (r: number, g: number, b: number) => `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;

		return {
			color: rgbToHex(data[0], data[1], data[2]),
			alpha: Math.round((data[3] / 255) * 100) / 100, // Convert to alpha value and round to at most 2 decimal places
		};
	}

	//#region color conversion algorithms courtesy of 
	// https://gist.github.com/mjackson/5311256#file-color-conversion-algorithms-js 
	// https://stackoverflow.com/a/39077686/618726
	// https://stackoverflow.com/a/53936623/618726

	static isValidHex(hex: string)
	{
		return isString(hex) && /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);
	}
	
	/**
	 * Converts a hexadecimal color code to an RGBA object.
	 * @param hex The hexadecimal color string to convert
	 * @param alpha Optional alpha value (0-1)
	 * @returns An object with r, g, b, a properties
	 * 
	 * @example
	 * // Returns { r: 255, g: 0, b: 0, a: 100 }
	 * ColorUtils.hexToRGBA("#ff0000");
	 * 
	 * // Returns { r: 255, g: 0, b: 0, a: 50 }
	 * ColorUtils.hexToRGBA("#ff0000", 0.5);
	 */
	static hexToRGBA(hex: string, alpha?: number)
	{


		function getChunksFromString(st: string, chunkSize: number) 
		{
			return st.match(new RegExp(`.{${chunkSize}}`, "g"));
		}

		function convertHexUnitTo256(hexStr: string)
		{
			return parseInt(hexStr.repeat(2 / hexStr.length), 16);
		}

		function getAlphafloat(a, alpha)
		{
			if (typeof a !== "undefined") 
			{ 
				return a / 255 * 100; 
			}
			if ((typeof alpha != "number") || alpha < 0 || alpha > 1)
			{
				return 100;
			}
			return alpha;
		}

		if (!ColorUtils.isValidHex(hex)) return ColorUtils.hexToRGBA("#ffffff", 100);

		const chunkSize = Math.floor((hex.length - 1) / 3);
		let hexArr = getChunksFromString(hex.slice(1), chunkSize);
		if(!hexArr) hexArr = ["ff", "ff", "ff", "ff"];
		const [r, g, b, a] = hexArr.map(convertHexUnitTo256);
		return { r: r, g: g, b: b, a: getAlphafloat(a, alpha) };
	}

	static rgbaToHex({r, g, b, a})
	{
		let output = '#' + [r, g, b].map(v => 
		{
			const hex = v.toString(16);
			return hex.length === 1 ? '0' + hex : hex;
		}).join('');

		// alpha special case
		if(a === undefined || a === null || Number.isNaN(a)) 
		{
			output += "ff";
		}
		else if(a > 0 && a <= 100)
		{
			output += Math.round(a / 100 * 255).toString(16);
		}
		else if (a > 100 && a <= 255)
		{
			output += Math.round(a / 255).toString(16);
		}

		return output;
	}

	static rgbToHex(color)
	{
		return "#" + ((1 << 24) + (color.r << 16) + (color.g << 8) + color.b).toString(16).slice(1);
	}

	static hexToRgb(hex: string): IColor
	{
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result
			?
			{
				r: parseInt(result[1], 16),
				g: parseInt(result[2], 16),
				b: parseInt(result[3], 16)
			}
			:
			{
				r: 0,
				g: 0,
				b: 0
			}
	}

	/**
	* Converts an RGB color value to HSL. Conversion formula
	* adapted from http://en.wikipedia.org/wiki/HSL_color_space.
	* Assumes r, g, and b are contained in the set [0, 255] and
	* returns h, s, and l in the set [0, 1].
	*
	* @param   Number  r       The red color value
	* @param   Number  g       The green color value
	* @param   Number  b       The blue color value
	* @return  Array           The HSL representation
	*/
	static rgbToHsl(r: number, g: number, b: number)
	{
		r /= 255, g /= 255, b /= 255;

		const max = Math.max(r, g, b), min = Math.min(r, g, b);
		let h = 0;
		let s: number;
		const l: number = (max + min) / 2;

		if (max == min)
		{
			h = s = 0; // achromatic
		}
		else
		{
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

			switch (max)
			{
				case r: h = (g - b) / d + (g < b ? 6 : 0); break;
				case g: h = (b - r) / d + 2; break;
				case b: h = (r - g) / d + 4; break;
			}

			h /= 6;
		}

		return [h, s, l];
	}

	/**
	* Converts an HSL color value to RGB. Conversion formula
	* adapted from http://en.wikipedia.org/wiki/HSL_color_space.
	* Assumes h, s, and l are contained in the set [0, 1] and
	* returns r, g, and b in the set [0, 255].
	*
	* @param   Number  h       The hue
	* @param   Number  s       The saturation
	* @param   Number  l       The lightness
	* @return  Array           The RGB representation
	*/
	static hslToRgb(h: number, s: number, l: number)
	{
		let r: number, g: number, b: number;

		if (s == 0)
		{
			r = g = b = l; // achromatic
		}
		else
		{
			function hue2rgb(p, q, t)
			{
				if (t < 0) t += 1;
				if (t > 1) t -= 1;
				if (t < 1 / 6) return p + (q - p) * 6 * t;
				if (t < 1 / 2) return q;
				if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
				return p;
			}

			const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
			const p = 2 * l - q;

			r = hue2rgb(p, q, h + 1 / 3);
			g = hue2rgb(p, q, h);
			b = hue2rgb(p, q, h - 1 / 3);
		}

		return [r * 255, g * 255, b * 255];
	}

	/**
	* Converts an RGB color value to HSV. Conversion formula
	* adapted from http://en.wikipedia.org/wiki/HSV_color_space.
	* Assumes r, g, and b are contained in the set [0, 255] and
	* returns h, s, and v in the set [0, 1].
	*
	* @param   Number  r       The red color value
	* @param   Number  g       The green color value
	* @param   Number  b       The blue color value
	* @return  Array           The HSV representation
	*/
	static rgbToHsv(r: number, g: number, b: number)
	{
		r /= 255, g /= 255, b /= 255;

		const max = Math.max(r, g, b), min = Math.min(r, g, b);
		const d = max - min;
		let h = 0;
		const s = max == 0 ? 0 : d / max;
		const v = max;

		if (max == min)
		{
			h = 0; // achromatic
		}
		else
		{
			switch (max)
			{
				case r: h = (g - b) / d + (g < b ? 6 : 0); break;
				case g: h = (b - r) / d + 2; break;
				case b: h = (r - g) / d + 4; break;
			}

			h /= 6;
		}

		return [h, s, v];
	}

	/**
	* Converts an HSV color value to RGB. Conversion formula
	* adapted from http://en.wikipedia.org/wiki/HSV_color_space.
	* Assumes h, s, and v are contained in the set [0, 1] and
	* returns r, g, and b in the set [0, 255].
	*
	* @param   Number  h       The hue
	* @param   Number  s       The saturation
	* @param   Number  v       The value
	* @return  Array           The RGB representation
	*/
	static hsvToRgb(h: number, s: number, v: number)
	{
		let r = 1, g = 1, b = 1;

		const i = Math.floor(h * 6);
		const f = h * 6 - i;
		const p = v * (1 - s);
		const q = v * (1 - f * s);
		const t = v * (1 - (1 - f) * s);

		switch (i % 6)
		{
			case 0: r = v, g = t, b = p; break;
			case 1: r = q, g = v, b = p; break;
			case 2: r = p, g = v, b = t; break;
			case 3: r = p, g = q, b = v; break;
			case 4: r = t, g = p, b = v; break;
			case 5: r = v, g = p, b = q; break;
		}

		return [r * 255, g * 255, b * 255];
	}
	//#endregion
}